import { InjectVuexModule, InjectModule } from 'vue-di/vuex';
import { Resolve } from 'vue-di';
import { PayorV4Api, EdgeEnrollmentApi, EdgeDocumentsApi, EdgeClaimApi } from '@trialcard/apigateway.client';
import { Action } from 'vuex-module-decorators';
import moment, { Moment } from 'moment';
import { orderBy } from 'lodash';
import { ClaimMedicalClaimSearchModel, MedicalClaimSearchResponseModel, PharmacyClaimSearchResponseModel, EnrollmentModel, ClaimAccumulatorModel } from '@trialcard/apigateway.models';
import { EnrollmentType } from '@trialcard/enums';
import { AxiosInstance, AxiosRequestConfig } from 'axios';
import PrescriberModule from './prescriber';

@InjectModule({ stateFactory: true }, module)
export default class AccountModule extends InjectVuexModule {
	@Resolve
	prescriberModule!: PrescriberModule;
	@Resolve
	payorApi!: PayorV4Api;
	@Resolve
	enrollmentApi!: EdgeEnrollmentApi;
	@Resolve
	documentApi!: EdgeDocumentsApi;
	@Resolve
	claimApi!: EdgeClaimApi;

	@Action({})
	async getSiteAccountPrescribers() {
		return this.prescriberModule.getPrescribersAPI();
	}

	@Action({})
	async getPatientAccountPayor(payorId: number) {
		try {
			const getPayorResponse = await this.payorApi.payorGetByTenantId(payorId);
			return getPayorResponse.data && getPayorResponse.data.data;
		} catch (error) {
			console.log(error);
			return;
		}
	}

	@Action({})
	async getEnrollment(patient: { firstName: string; lastName: string; dateOfBirth: string; zip: string }) {
		try {
			let bestActiveEnrollment;
			const getBestEnrollmentResponse = await this.enrollmentApi.enrollmentGetBestEnrollmentsV2({
				...patient,
			} as any);
			const enrollments = getBestEnrollmentResponse.data && getBestEnrollmentResponse.data.data;
			const activeEnrollments =
				enrollments &&
				enrollments.filter(e => {
					return moment(e.endDate!) > moment(Date.now());
				});
			if (activeEnrollments && activeEnrollments.length > 0) {
				bestActiveEnrollment = orderBy(activeEnrollments, enrl => moment(enrl.endDate).valueOf(), 'asc')[0];
			} else {
				bestActiveEnrollment = enrollments && orderBy(enrollments, enrl => moment(enrl.endDate).valueOf(), 'desc')[0];
			}
			return bestActiveEnrollment;
		} catch (error) {
			console.log(error);
			return;
		}
	}

	@Action({})
	async uploadDocument(request: { file: File; documentTypeId: string | null }) {
		try {
			const form = new FormData();
			if (request.documentTypeId) form.append('type', request.documentTypeId);
			form.append('attachment', request.file);
			const axios: AxiosInstance = (this.documentApi as any).axios;
			const axiosRequest: AxiosRequestConfig = {
				url: 'edge/documents/v1/Document/upload',
				method: 'POST',
				data: form,
				headers: {
					'x-program-id': process.env.programId!,
				},
			};
			await Promise.all([
				(this.documentApi as any).authentications.ApiKey.applyToRequest(axiosRequest),
				(this.documentApi as any).authentications.Bearer.applyToRequest(axiosRequest),
				(this.documentApi as any).authentications.Token.applyToRequest(axiosRequest),
			]);
			await axios.request(axiosRequest);
			return true;
		} catch (error) {
			console.log(error);
			return false;
		}
	}

	@Action({})
	async getMedicalClaims(memberNumber: string) {
		try {
			const getClaimsResponse = await this.claimApi.claimMedicalClaimSearch(
				{
					searchModel: {
						memberNumbers: [
							memberNumber
						]
					} as ClaimMedicalClaimSearchModel,
					filterByClaimGroup: true
				})
			const claims = getClaimsResponse.data && getClaimsResponse.data.data;
			const response: MedicalClaimSearchResponseModel[] = claims || [];
			return response;
		} catch (error) {
			console.log(error);
			return [];
		}
	}

	@Action({})
	async getPharmacyClaims(memberNumber: string) {
		try {
			const getClaimsResponse = await this.claimApi.claimPharmacyClaimSearch(
				{
					searchModel: {
						memberNumbers: [
							memberNumber
						]
					} as ClaimMedicalClaimSearchModel,
					filterByClaimGroup: true
				})
			const claims = getClaimsResponse.data && getClaimsResponse.data.data;
			const response: PharmacyClaimSearchResponseModel[] = claims || [];
			return response;
		} catch (error) {
			console.log(error);
			return [];
		}
	}

	@Action({})
	async getClaims(enrollment: EnrollmentModel) {
		const enrollmentMedicalClaims: MedicalClaimSearchResponseModel[] = [];
		if (enrollment && enrollment.memberNumbers) {
			// Get Claims - Medical Mn only for G1
			const medicalMemberNumber = enrollment.memberNumbers.find(x => {
				return x.type === EnrollmentType.Medical;
			});
			if (medicalMemberNumber) {
				const medicalClaims = await this.getMedicalClaims(medicalMemberNumber.number!);
				enrollmentMedicalClaims.push(...medicalClaims);
			}
		}
		return {
			claims: enrollmentMedicalClaims
		}
	}

	@Action({})
	async getAccumulatorsByMemberNumber(memberNumber: string) {
		try {
			const getAccumulatorResponse = await this.claimApi.claimAccumulatorSearch({
				memberNumbers: [memberNumber]
			})
			const accumulators = getAccumulatorResponse.data && getAccumulatorResponse.data.data
			return accumulators || [];

		} catch (error) {
			console.log(error);
			return [];
		}
	}
}
